import { Create } from "@refinedev/mui";
import {
  Box,
  TextField,
  FormControlLabel,
  Grid,
  Card,
  CardContent,
  Typography,
  Button,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Switch,
} from "@mui/material";
import { useForm } from "@refinedev/react-hook-form";
import {
  IResourceComponentsProps,
  useTranslate,
  useApiUrl,
} from "@refinedev/core";
import { Controller } from "react-hook-form";
import { DateTimePicker } from "@mui/x-date-pickers";
import { LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import React, { useState } from "react";
import { parseISO } from "date-fns";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CancelIcon from "@mui/icons-material/Cancel";
import axiosInstance from "../../api/axios-config";
import { UserSearchResult } from "../../interfaces/search";
import { UserSearch } from "../../components/search/search";
import AddressAutocomplete, { GeoapifyResult } from "../../components/forms/AddressAutocomplete";
import LocationMap from "../../components/forms/LocationMap";

export const EventCreate: React.FC<IResourceComponentsProps> = () => {
  const translate = useTranslate();
  const apiUrl = useApiUrl();

  // Security list state management
  const [securityList, setSecurityList] = useState<
    Array<{
      value: string;
      type: "email" | "phone";
      exists: boolean | null;
      name?: string;
      email?: string;
      phone?: string;
      id?: number;
    }>
  >([]);
  const [inputError, setInputError] = useState<string | null>(null);

  const {
    saveButtonProps,
    refineCore: { formLoading, onFinish },
    register,
    control,
    formState: { errors },
    setValue,
    handleSubmit,
    getValues,
    watch,
  } = useForm({
    refineCoreProps: {
      resource: "safe-events",
      action: "create",
      redirect: "list",
      onMutationError: (error) => {
        console.error("Error creating event:", error);
      },
    },
  });

  // Set default values
  React.useEffect(() => {
    const now = new Date();
    const tomorrow = new Date(now);
    tomorrow.setDate(tomorrow.getDate() + 1);

    // Set default values using setValue from react-hook-form
    setValue("name", `Event ${now.toLocaleDateString()}`);
    setValue("description", "");
    setValue("latitude", 43.2862717);
    setValue("longitude", 5.3832961);
    setValue("geofenceRadius", 100);

    // Initialize map coordinates
    setMapCoordinates({
      latitude: 43.2862717,
      longitude: 5.3832961,
    });
  }, [setValue]);

  // Watch fields for changes from user input
  React.useEffect(() => {
    const subscription = watch((value, { name }) => {
      if (name === 'latitude' || name === 'longitude') {
        const lat = value.latitude;
        const lng = value.longitude;
        if (lat && lng && !isNaN(lat) && !isNaN(lng)) {
          setMapCoordinates({
            latitude: lat,
            longitude: lng
          });
        }
      }
    });

    return () => subscription.unsubscribe();
  }, [watch]);

  // Validation functions for email and phone
  const isValidEmail = (email: string) => {
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
  };

  const isValidPhone = (phone: string) => {
    // Basic phone validation - can be enhanced as needed
    return /^\+?[0-9]{10,15}$/.test(phone);
  };

  // Check if user exists in database
  const checkUserExists = async (value: string, type: "email" | "phone") => {
    try {
      const endpoint = `${apiUrl}/users/check`;
      const response = await axiosInstance.get(endpoint, {
        params: { [type]: value },
      });
      return !!response.data.exists;
    } catch (error) {
      console.error("Error checking user existence:", error);
      return false;
    }
  };

  // Handle adding user from search results to security list
  // Update the handleAddUserFromSearch function
  const handleAddUserFromSearch = async (user: UserSearchResult) => {
    const value = user.email || user.phone;
    const type = user.email ? "email" : "phone";

    if (!value) {
      setInputError("User has no email or phone number");
      return;
    }

    // Check if already in the list
    if (securityList.some((item) => item.value === value)) {
      setInputError("This entry is already in the list");
      return;
    }

    // Save user ID as well as other details
    setSecurityList((prev) => [
      ...prev,
      {
        value,
        type,
        exists: true,
        name: user.fullname,
        email: user.email,
        phone: user.phone,
        id: user.id,
      },
    ]);
  };

  // Handle manual entry
  const handleManualAdd = async (value: string, type: "email" | "phone") => {
    if (securityList.some((item) => item.value === value)) {
      setInputError("This entry is already in the list");
      return;
    }

    setSecurityList((prev) => [...prev, { value, type, exists: null }]);

    // Check existence
    const exists = await checkUserExists(value, type);

    setSecurityList((prev) =>
      prev.map((item) => (item.value === value ? { ...item, exists } : item))
    );
  };

  // Remove security item from list
  const handleRemoveSecurity = (value: string) => {
    setSecurityList((prev) => prev.filter((item) => item.value !== value));
  };

  // Format the submitted data to match what the backend expects
  const onSubmit = async (data: any) => {
    // Make a copy we can transform
    const submitData = { ...data };

    // Handle date formatting
    if (submitData.startDate instanceof Date) {
      submitData.startDate = submitData.startDate.toISOString();
    } else if (typeof submitData.startDate === "string") {
      try {
        submitData.startDate = parseISO(submitData.startDate).toISOString();
      } catch (error) {
        console.error("Error parsing startDate string:", error);
        submitData.startDate = new Date().toISOString();
      }
    }

    if (submitData.endDate instanceof Date) {
      submitData.endDate = submitData.endDate.toISOString();
    } else if (typeof submitData.endDate === "string") {
      try {
        submitData.endDate = parseISO(submitData.endDate).toISOString();
      } catch (error) {
        console.error("Error parsing endDate string:", error);
        submitData.endDate = new Date(
          Date.now() + 24 * 60 * 60 * 1000
        ).toISOString();
      }
    }

    // Extract actual user IDs from security list
    const securityContactIds = securityList
      .filter((item) => item.exists === true) // Only include existing users
      .map((item) => {
        // If the item has an explicit ID from search results, use that
        if (item.id) return item.id;
        return null;
      })
      .filter(Boolean); // Remove any undefined or null values

    // Add securityContactIds to the submission data
    if (securityContactIds.length > 0) {
      // The primary format expected by the API is likely "securityContacts"
      submitData.securityContacts = securityContactIds.map(id => ({ id }));

      // Also include as flat array in case that's what the API expects
      submitData.securityContactIds = securityContactIds;
    }

    // Perform the actual form submission
    try {
      const result = await onFinish(submitData);
      return result;
    } catch (error) {
      console.error("Error submitting event:", error);
      throw error;
    }
  };

  // Track added users by ID to disable their "Add" buttons
  const getAddedUserIds = () => {
    // We don't have direct IDs in securityList, but we can collect emails and phones
    // to check against search results
    const emails = securityList
      .filter((item) => item.email)
      .map((item) => item.email as string);

    const phones = securityList
      .filter((item) => item.phone)
      .map((item) => item.phone as string);

    // Also include values from type=email or type=phone
    securityList.forEach((item) => {
      if (item.type === "email") {
        emails.push(item.value);
      } else if (item.type === "phone") {
        phones.push(item.value);
      }
    });

    // Return object of emails and phones which the search component can check against
    return {
      emails,
      phones,
    };
  };

  const [mapCoordinates, setMapCoordinates] = useState({
    latitude: 43.2862717,
    longitude: 5.3832961,
  });

  return (
    <Create
      isLoading={formLoading}
      saveButtonProps={{
        ...saveButtonProps,
        onClick: (e) => {
          e.preventDefault();
          e.stopPropagation();
          handleSubmit(async (data) => {
            try {
              await onSubmit(data);
              return false;
            } catch (error) {
              console.error("Error during form submission:", error);
              return false;
            }
          })(e);
          return false;
        },
      }}
    >
      <Box
        component="form"
        sx={{ display: "flex", flexDirection: "column" }}
        autoComplete="off"
        onSubmit={(e) => {
          e.preventDefault();
          e.stopPropagation();
          handleSubmit(async (data) => {
            try {
              await onSubmit(data);
              return false;
            } catch (error) {
              console.error("Error during form submission:", error);
              return false;
            }
          })(e);
          return false;
        }}
      >

        <Grid container spacing={2}>
          {/* First column with event details */}
          <Grid item xs={12} md={6}>
            <Card>
              <CardContent>
                <TextField
                  {...register("name", {
                    required: "This field is required",
                  })}
                  error={!!(errors as any)?.name}
                  helperText={(errors as any)?.name?.message}
                  margin="normal"
                  fullWidth
                  InputLabelProps={{ shrink: true }}
                  type="text"
                  label={translate("events.fields.name", "Event Name")}
                  name="name"
                />

                <TextField
                  {...register("description")}
                  error={!!(errors as any)?.description}
                  helperText={(errors as any)?.description?.message}
                  margin="normal"
                  fullWidth
                  InputLabelProps={{ shrink: true }}
                  type="text"
                  label={translate("events.fields.description", "Description")}
                  name="description"
                  multiline
                  rows={3}
                />

                <LocalizationProvider dateAdapter={AdapterDateFns}>
                  <Box sx={{ display: 'flex', flexDirection: 'row', gap: 2 }}>
                  <Controller
                    control={control}
                    name="startDate"
                    rules={{ required: "This field is required" }}
                    render={({ field }) => (
                      <DateTimePicker
                      label={translate(
                        "events.fields.startDate",
                        "Start Date"
                      )}
                      value={field.value}
                        onChange={field.onChange}
                        slotProps={{
                          textField: {
                            margin: "normal",
                            fullWidth: true,
                            error: !!(errors as any)?.startDate,
                            helperText: (errors as any)?.startDate?.message,
                          },
                        }}
                        />
                      )}
                      />

                  <Controller
                    control={control}
                    name="endDate"
                    render={({ field }) => (
                      <DateTimePicker
                      {...field}
                      label={translate("events.fields.endDate", "End Date")}
                      slotProps={{
                        textField: {
                          fullWidth: true,
                          margin: "normal",
                          error: !!(errors as any)?.endDate,
                          helperText: (errors as any)?.endDate?.message,
                        },
                      }}
                      minDateTime={new Date()}
                      />
                    )}
                    />
                  </Box>
                </LocalizationProvider>
                <Box sx={{ marginTop: 1 }}>
                  <Controller
                    control={control}
                    name="isActive"
                    defaultValue={true}
                    render={({ field }) => (
                      <FormControlLabel
                        label={translate("isActive.title", "Active")}
                        control={
                          <Switch
                            {...field}
                            checked={field.value}
                            onChange={(event) => {
                              field.onChange(event.target.checked);
                            }}
                            color="primary"
                          />
                        }
                      />
                    )}
                  />
                </Box>
              </CardContent>
            </Card>
          </Grid>

          {/* Second column with date/time and location */}
          <Grid item xs={12} md={6}>
            <Card>
              <CardContent>
                <Box sx={{ display: 'flex', flexDirection: 'row', gap: 2, alignItems: 'flex-end' }}>
                  <Box sx={{ flex: 1 }}>
                    <AddressAutocomplete
                      label={translate("events.fields.address", "Address")}
                      placeholder="Search for an address"
                      onAddressSelect={(result: GeoapifyResult) => {
                        // Update latitude and longitude values when an address is selected
                        setValue("latitude", result.lat);
                        setValue("longitude", result.lon);

                        // Force re-render of the map by updating the state
                        setMapCoordinates({
                          latitude: result.lat,
                          longitude: result.lon
                        });


                      }}
                    />
                  </Box>
                  <Box sx={{ width: '100px' }}>
                    <TextField
                      {...register("geofenceRadius", {
                        required: "This field is required",
                        valueAsNumber: true,
                      })}
                      error={!!(errors as any)?.geofenceRadius}
                      helperText={(errors as any)?.geofenceRadius?.message}
                      margin="normal"
                      fullWidth
                      InputLabelProps={{ shrink: true }}
                      type="number"
                      label={translate(
                        "events.fields.radius",
                        "Geofence Radius (m)"
                      )}
                      name="geofenceRadius"
                      sx={{ marginBottom: 0 }}
                      onChange={(e) => {
                        register("geofenceRadius").onChange(e);
                        // Force a re-render of the map with the new radius
                        setTimeout(() => {
                          setMapCoordinates({...mapCoordinates});
                        }, 10);
                      }}
                    />
                  </Box>
                </Box>

                <Box sx={{ mt: 2, height: "400px", borderRadius: '6px' }}>
                  <Box sx={{ height: "100%", borderRadius: '6px', overflow: 'hidden' }}>
                    <LocationMap
                      latitude={mapCoordinates.latitude || getValues("latitude") || 43.2862717}
                      longitude={mapCoordinates.longitude || getValues("longitude") || 5.3832961}
                      radius={parseInt(getValues("geofenceRadius") || "100")}
                      onLocationChange={(lat, lng) => {
                        setValue("latitude", lat);
                        setValue("longitude", lng);
                      }}
                      showCircleOnLoad={false}
                      showMarker={false}
                    />
                  </Box>
                </Box>

                {/* Hidden inputs for latitude and longitude that still register with the form */}
                <input
                  type="hidden"
                  {...register("latitude", {
                    required: "This field is required",
                    valueAsNumber: true,
                  })}
                />
                <input
                  type="hidden"
                  {...register("longitude", {
                    required: "This field is required",
                    valueAsNumber: true,
                  })}
                />
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} md={6}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Add Security Access
                </Typography>
                <UserSearch
                  onUserSelect={handleAddUserFromSearch}
                  onManualAdd={handleManualAdd}
                  apiUrl={apiUrl}
                  inputLabel="Search for security personnel"
                  showManualAdd={true}
                  addedContacts={getAddedUserIds()}
                />
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} md={6}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Security Access List
                </Typography>
                {securityList.length > 0 ? (
                  <List dense>
                    {securityList.map((item, index) => (
                      <ListItem
                        key={index}
                        secondaryAction={
                          <Button
                            size="small"
                            color="error"
                            onClick={() => handleRemoveSecurity(item.value)}
                          >
                            Remove
                          </Button>
                        }
                      >
                        <ListItemIcon>
                          {item.exists === null ? (
                            <span>⏳</span>
                          ) : item.exists ? (
                            <CheckCircleIcon color="success" />
                          ) : (
                            <CancelIcon color="error" />
                          )}
                        </ListItemIcon>
                        <ListItemText
                          primary={item.name ? item.name : item.value}
                          // Use a simple string instead of Typography components for secondary text
                          secondary={
                            item.email && item.phone
                              ? `Email: ${item.email}\nPhone: ${item.phone}`
                              : item.email
                              ? `Email: ${item.email}`
                              : item.phone
                              ? `Phone: ${item.phone}`
                              : `${
                                  item.type === "email" ? "Email" : "Phone"
                                }: ${item.value}`
                          }
                        />
                      </ListItem>
                    ))}
                  </List>
                ) : (
                  <Typography
                    variant="body1"
                    color="textSecondary"
                    align="center"
                    sx={{ py: 3 }}
                  >
                    No security personnel added yet.
                  </Typography>
                )}
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      </Box>
    </Create>
  );
};
